import { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { Box, Heading, Modal, Paragraph, Tabs } from '@zillow/constellation-10';
import { useRentalManagerUserPropertyVrModelWebApiHandler_getAllVrModels } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { StyledTab, StyledTabsList } from './styles';

import ErrorMessageConstants from '../../../constants/ErrorMessageConstants';
import Constants from '../../../constants';

import VrModel from '../../../model/VrModel';

import ViewTourModal from '../ViewTourModal';
import SpinnerContainer from '../../SpinnerContainer';
import ZillowTours from './components/ZillowTours';
import ExternalTour from './components/ExternalTour';
import ModalFooter from '../ModalFooter';

import { useGetExternalTourProviders } from '../../../hooks/queries/useGetExternalTourProviders';

const propTypes = {
  defaultVrModels: PropTypes.arrayOf(
    PropTypes.shape({
      vrModelId: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      createDateTimeUtc: PropTypes.string.isRequired,
      creatorName: PropTypes.string.isRequired,
      posterUrl: PropTypes.string,
      tourUrl: PropTypes.string,
      status: PropTypes.oneOf(['published', 'unpublished']),
    }),
  ),
  shouldLoadVrModels: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  tourType: PropTypes.oneOf(['floorPlan', 'unit', 'amenity', 'singleFamily']),
  initialSelectedVrModelId: PropTypes.string,
  initialExternalTour: PropTypes.shape({
    label: PropTypes.string,
    tourUrl: PropTypes.string,
  }),
  initialTab: PropTypes.oneOf(['zillow-tour-tab', 'external-tour-tab']),
  zillowDisplayStatus: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

const defaultProps = {
  zillowDisplayStatus: '',
  defaultVrModels: [],
  shouldLoadVrModels: true,
  tourType: 'floorPlan',
  initialSelectedVrModelId: undefined,
  initialExternalTour: {
    label: '',
    tourUrl: '',
  },
  initialTab: 'zillow-tour-tab',
};

const SF_NOT_PUBLISHED_TEXT =
  'We won’t able to add this 3d tour to your listing until the listing is live on our ' +
  'sites. Once this listing is live on our sites, please try this operation again.';

const SelectTourModal = ({
  filterOutVrModelIds,
  shouldLoadVrModels,
  onSubmit,
  onCancel,
  tourType,
  defaultVrModels,
  initialSelectedVrModelId,
  initialExternalTour,
  initialTab,
  zillowDisplayStatus,
  onClose,
}) => {
  const externalTourInputRef = useRef(null);

  const listingAlias = useSelector((state) => state.properties.currentProperty.listingAlias);

  const {
    data: vrModelsData,
    isLoading: isLoadingVrModels,
    error: vrModelsError,
  } = useRentalManagerUserPropertyVrModelWebApiHandler_getAllVrModels(
    { listingAlias },
    { enabled: shouldLoadVrModels },
  );

  const vrModels = useMemo(() => {
    if (!shouldLoadVrModels) return defaultVrModels;
    const loadedVrModels = (vrModelsData?.vrModels ?? []).map((vm) => VrModel(vm));
    const filteredVrModels =
      tourType === 'amenity'
        ? loadedVrModels.filter(
            (vm) => vm.vrModelId === initialSelectedVrModelId || filterOutVrModelIds.indexOf(vm.vrModelId) === -1,
          )
        : loadedVrModels;
    return filteredVrModels.sort((a, b) => (a.createDateTimeUtc < b.createDateTimeUtc ? 1 : -1));
  }, [vrModelsData, shouldLoadVrModels, defaultVrModels, tourType, filterOutVrModelIds, initialSelectedVrModelId]);

  const [selectedTab, setSelectedTab] = useState(initialTab);
  const [selectedTabToConfirm, setSelectedTabToConfirm] = useState(initialTab);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [tourUrlErrorMessage, setTourUrlErrorMessage] = useState('');
  const [selectedVrModelId, setSelectedVrModelId] = useState(initialSelectedVrModelId);
  const [externalTour, setExternalTour] = useState(initialExternalTour);
  const [showViewTourModal, setShowViewTourModal] = useState(false);
  const [vrModel, setVrModel] = useState();

  const isListingPublished = Constants.LISTING_STATUS.POSTED.toLowerCase() === zillowDisplayStatus;
  const isLoading = shouldLoadVrModels && isLoadingVrModels;
  const vrModelsErrorMessage = vrModelsError ? 'Cannot retrieve 3d tours, please try again later.' : undefined;

  /* Prefetching data to avoid a loading spinner on the external tours tab */
  useGetExternalTourProviders();

  const handleSubmit = () => {
    if (selectedTab === 'zillow-tour-tab') {
      if (tourType === 'singleFamily' && !isListingPublished) {
        setErrorMessage(SF_NOT_PUBLISHED_TEXT);
        return;
      }
    }
    setIsSubmitting(true);
    return onSubmit(selectedTab === 'zillow-tour-tab', selectedVrModelId, externalTour)
      .then(() => {
        onClose();
      })
      .catch((errMessage) => {
        setIsSubmitting(false);
        if (
          errMessage === ErrorMessageConstants.EXTERNAL_TOUR_INVALID_URL ||
          errMessage === ErrorMessageConstants.EXTERNAL_TOUR_ALREADY_EXIST ||
          errMessage === ErrorMessageConstants.EXTERNAL_TOUR_NOT_SUPPORTED_PROVIDER
        ) {
          setTourUrlErrorMessage(errMessage);
          // ZRM-10774: for error accessibility on screen readers, set focus to the tour url input
          externalTourInputRef.current && externalTourInputRef.current.focus();
        } else {
          setErrorMessage(errMessage);
        }
      });
  };

  const handleSelectTour = (id) => {
    setErrorMessage(null);
    const isUnselecting = selectedVrModelId === id;
    setSelectedVrModelId(isUnselecting ? null : id);
  };

  const handleViewTour = (vrModel) => {
    setVrModel(vrModel);
    setShowViewTourModal(true);
  };

  const handleUpdateExternalTour = (key, value) => {
    if (key === 'tourUrl' && tourUrlErrorMessage) {
      setTourUrlErrorMessage('');
    }
    if (errorMessage) {
      setErrorMessage('');
    }
    setExternalTour({
      ...externalTour,
      [key]: value,
    });
  };

  const handleConfirmTabChange = () => {
    setSelectedTab(selectedTabToConfirm);
    setSelectedVrModelId(initialSelectedVrModelId);
    setExternalTour(initialExternalTour);
    setTourUrlErrorMessage('');
    setErrorMessage('');
    setShowConfirmationModal(false);
  };

  const handleSelectTab = (selected) => {
    const hasPendingChanges =
      selectedVrModelId !== initialSelectedVrModelId ||
      externalTour.tourUrl !== initialExternalTour.tourUrl ||
      externalTour.label !== initialExternalTour.label;
    if (hasPendingChanges) {
      setShowConfirmationModal(true);
      setSelectedTabToConfirm(selected);
    } else {
      setSelectedTab(selected);
    }
  };

  const vrModelChange = initialSelectedVrModelId !== selectedVrModelId;
  const externalTourChange =
    initialExternalTour.tourUrl !== externalTour.tourUrl || initialExternalTour.label !== externalTour.label;
  const displayErrorMessage = errorMessage || vrModelsErrorMessage;
  const isSubmitDisabled =
    !!displayErrorMessage ||
    !!tourUrlErrorMessage ||
    (!vrModelChange && !externalTourChange) ||
    (selectedTab === 'external-tour-tab' && (!externalTour.tourUrl || !externalTour.label));

  return (
    <Modal
      header={<Heading level={2}>3D tour</Heading>}
      body={
        isLoading ? (
          <SpinnerContainer />
        ) : (
          <Box
            css={{
              marginTop: 'spacing.-400',
              md: {
                minHeight: '600px',
              },
            }}
          >
            <Tabs.Root selected={selectedTab} onSelectedChange={handleSelectTab}>
              <StyledTabsList aria-label="tour tabs">
                <StyledTab value="zillow-tour-tab">From Zillow 3D Home app</StyledTab>
                <StyledTab value="external-tour-tab">With a link</StyledTab>
              </StyledTabsList>
              <Tabs.Panel value="zillow-tour-tab">
                <ZillowTours
                  onSelectTour={handleSelectTour}
                  onViewTour={handleViewTour}
                  vrModels={vrModels}
                  selectedVrModelId={selectedVrModelId}
                  tourType={tourType}
                />
              </Tabs.Panel>
              <Tabs.Panel value="external-tour-tab">
                <ExternalTour
                  externalTour={externalTour}
                  onUpdateExternalTour={handleUpdateExternalTour}
                  tourUrlErrorMessage={tourUrlErrorMessage}
                  externalTourInputRef={externalTourInputRef}
                />
              </Tabs.Panel>
            </Tabs.Root>
            <Modal
              open={showConfirmationModal}
              body={<Paragraph>If you change tabs now, all changes will be lost. Is this what you want?</Paragraph>}
              footer={
                <ModalFooter
                  onCancel={() => setShowConfirmationModal(false)}
                  onSubmit={handleConfirmTabChange}
                  submitText="Yes, discard changes"
                />
              }
            />
            {showViewTourModal && <ViewTourModal vrModel={vrModel} onClose={() => setShowViewTourModal(false)} />}
          </Box>
        )
      }
      footer={
        <ModalFooter
          onCancel={onCancel}
          onSubmit={handleSubmit}
          submitText="Save"
          isSubmitting={isSubmitting}
          errorMessage={displayErrorMessage}
          hasError={!!displayErrorMessage}
          isSubmitDisabled={isSubmitDisabled}
        />
      }
      onOpenChange={(open) => !open && onClose()}
      open={true}
      size={{ base: 'fullScreen', lg: 'md' }}
    />
  );
};

SelectTourModal.propTypes = propTypes;
SelectTourModal.defaultProps = defaultProps;

export default SelectTourModal;
